/** How far through each level a learner is, and the button that finishes one.
 *
 * The client asked for "a button at the end of the course, after completing
 * all lessons, practice, then user can mark the level completed".
 *
 * The button only appears once everything is genuinely done — but it is
 * still a button, not an automatic tick. Deciding you are finished is the
 * point of the moment.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/levels";
import { ApiError } from "../../api/http";
import { Badge, Button, Card, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";

export function LevelProgress() {
  const t = useT();
  const { push } = useToast();
  const [levels, setLevels] = useState<api.LevelStatus[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api.myLevels().then(setLevels).catch(() => setLevels([]));
  }, []);

  if (levels === null) {
    return (
      <div className="grid place-items-center py-8">
        <Spinner size={24} />
      </div>
    );
  }

  // A level with nothing in it is not a level a learner can work on.
  const shown = levels.filter((l) => l.lessons_total > 0 || l.parts_total > 0);
  if (shown.length === 0) return null;

  const replace = (row: api.LevelStatus) =>
    setLevels((all) => (all ?? []).map((l) => (l.level === row.level ? row : l)));

  const claim = async (level: string) => {
    setBusy(level);
    try {
      replace(await api.completeLevel(level));
      push("success", t("levels.wellDone", { level }));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("common.error.generic"));
    } finally {
      setBusy(null);
    }
  };

  const undo = async (level: string) => {
    setBusy(level);
    try {
      replace(await api.uncompleteLevel(level));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("common.error.generic"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title={t("levels.title")}>
      <ul className="divide-y divide-line">
        {shown.map((l) => (
          <li key={l.level} className="flex flex-wrap items-center gap-3 py-3">
            <span className="w-10 text-lg font-extrabold text-ink">{l.level}</span>

            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted">
                {t("levels.lessons", { done: l.lessons_done, total: l.lessons_total })}
                {" · "}
                {t("levels.practice", {
                  done: l.parts_practised, total: l.parts_total,
                })}
              </p>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-canvas">
                <div
                  className="h-full bg-brand transition-all"
                  style={{ width: `${share(l) * 100}%` }}
                />
              </div>
            </div>

            {l.completed_at ? (
              <div className="flex items-center gap-2">
                <Badge tone="success">{t("levels.done")}</Badge>
                <Button
                  variant="ghost"
                  onClick={() => void undo(l.level)}
                  loading={busy === l.level}
                >
                  {t("levels.undo")}
                </Button>
              </div>
            ) : l.eligible ? (
              <Button onClick={() => void claim(l.level)} loading={busy === l.level}>
                {t("levels.markComplete")}
              </Button>
            ) : (
              <span className="text-xs text-muted">{t("levels.keepGoing")}</span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Lessons and practice parts weighted by how many there are of each, so a
 *  level with 76 lessons and 12 parts is not half-finished by touching one
 *  practice question. */
function share(l: api.LevelStatus): number {
  const total = l.lessons_total + l.parts_total;
  if (total === 0) return 0;
  return Math.min(1, (l.lessons_done + l.parts_practised) / total);
}
